import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Heart, Shield, Check, Loader2, ArrowLeft, Crown, Lock, Star, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import API_URL from '../config/api';

const PLANS = {
  premium: {
    name: 'Premium',
    price: '9.99',
    color: '#6E1A24',
    icon: Star,
    features: ['Unlimited Likes', 'Advanced Filters', 'Priority Support'],
  },
  gold: {
    name: 'Gold',
    price: '16.99',
    color: '#D4AF37',
    icon: Crown,
    features: ['Everything in Premium', 'See Who Liked You', 'Profile Boost', 'Priority Visibility', 'First Access to New Features'],
  },
};

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const plan = searchParams.get('plan') || 'premium';
  const planData = PLANS[plan];

  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  const currentPlan = useMemo(() => user?.subscription?.plan || user?.current_plan || 'free', [user]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/paypal/config`, { withCredentials: true });
        setPaypalConfig(res.data);
      } catch (err) {
        toast.error('Failed to load payment configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleApprove = useCallback(async (data) => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/paypal/activate`, {
        subscription_id: data.subscriptionID,
        plan,
      }, { withCredentials: true });

      setSuccess(true);
      toast.success(`Welcome to LusoLove ${planData.name}!`);
      await refreshUser();
      setTimeout(() => navigate('/discover'), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to activate subscription');
    } finally {
      setProcessing(false);
    }
  }, [plan, planData, refreshUser, navigate]);

  if (!planData) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#6C6C67] mb-4">Invalid plan selected</p>
          <Link to="/discover"><Button>Go Back</Button></Link>
        </div>
      </div>
    );
  }

  const PlanIcon = planData.icon;
  const paypalPlanId = paypalConfig?.plans?.[plan]?.plan_id;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="checkout-page">
      <SEOHead title={`Upgrade to ${planData.name}`} noindex path="/checkout" />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#6C6C67] hover:text-[#2D2D2A] transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#6E1A24]" strokeWidth={1.5} />
            <span className="font-serif text-[#2D2D2A]">LusoLove</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#9C9C97]">
            <Lock className="w-3.5 h-3.5" /> Secure
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8 sm:py-12">
        {/* Success State */}
        {success ? (
          <div className="text-center py-16" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#2D6A4F]" />
            </div>
            <h1 className="text-2xl font-serif font-medium text-[#2D2D2A] mb-2">Welcome to {planData.name}!</h1>
            <p className="text-[#6C6C67] mb-6">Your subscription is now active. Enjoy all premium features.</p>
            <Button onClick={() => navigate('/discover')} className="bg-[#6E1A24] text-white rounded-full px-8 py-3">
              Start Discovering
            </Button>
          </div>
        ) : (
          <>
            {/* Plan Card */}
            <div className="bg-white rounded-3xl border border-[#E5E3DB] overflow-hidden shadow-sm mb-8" data-testid="plan-card">
              <div className="p-8 text-center" style={{ background: `linear-gradient(135deg, ${planData.color}15, ${planData.color}05)` }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${planData.color}15` }}>
                  <PlanIcon className="w-8 h-8" style={{ color: planData.color }} />
                </div>
                <h1 className="text-2xl font-serif font-medium text-[#2D2D2A] mb-1" data-testid="plan-name">
                  LusoLove {planData.name}
                </h1>
                <div className="flex items-baseline justify-center gap-1 mt-3">
                  <span className="text-4xl font-bold" style={{ color: planData.color }}>€{planData.price}</span>
                  <span className="text-[#9C9C97] text-sm">/month</span>
                </div>
              </div>

              <div className="p-6 border-t border-[#E5E3DB]/50">
                <ul className="space-y-3">
                  {planData.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm text-[#2D2D2A]">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${planData.color}15` }}>
                        <Check className="w-3 h-3" style={{ color: planData.color }} />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Already on plan */}
            {currentPlan === plan ? (
              <div className="bg-[#F5F2EB] rounded-2xl p-6 text-center">
                <p className="text-[#6C6C67] text-sm">You're already on the {planData.name} plan</p>
                <Button onClick={() => navigate('/discover')} className="mt-4 bg-[#6E1A24] text-white rounded-full px-8 py-3">
                  Continue Exploring
                </Button>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#6E1A24]" />
              </div>
            ) : processing ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#6E1A24] mx-auto mb-4" />
                <p className="text-[#6C6C67]">Activating your subscription...</p>
              </div>
            ) : paypalConfig && paypalPlanId ? (
              <>
                {/* PayPal Buttons */}
                <div className="bg-white rounded-2xl border border-[#E5E3DB] p-6 mb-6" data-testid="paypal-buttons-container">
                  <p className="text-sm font-medium text-[#2D2D2A] mb-4 text-center">Choose your payment method</p>
                  <PayPalScriptProvider options={{
                    'client-id': paypalConfig.client_id,
                    vault: true,
                    intent: 'subscription',
                    currency: 'EUR',
                  }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', shape: 'pill', color: 'gold', label: 'subscribe' }}
                      createSubscription={(data, actions) => {
                        return actions.subscription.create({ plan_id: paypalPlanId });
                      }}
                      onApprove={handleApprove}
                      onError={(err) => { toast.error('Payment error. Please try again.'); }}
                      onCancel={() => { toast.info('Payment cancelled'); }}
                    />
                  </PayPalScriptProvider>
                </div>

                {/* Security badges */}
                <div className="flex items-center justify-center gap-4 mt-4 text-xs text-[#9C9C97]">
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" /> Secure checkout
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" /> Cancel anytime
                  </div>
                </div>

                <p className="text-center text-xs text-[#9C9C97] mt-4">
                  Subscription renews monthly. Cancel anytime from your settings.
                </p>

                {/* Switch plan */}
                <div className="text-center mt-6">
                  <Link to="/pricing" className="text-sm text-[#6C6C67] hover:text-[#2D2D2A] hover:underline font-medium transition-colors" data-testid="view-all-plans">
                    View all plans
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-[#6C6C67]">
                <p>Payment system unavailable. Please try again later.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
